import { EventEmitter } from 'events';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../../utils/logger';
import {
  Context,
  ContextItem,
  ContextType,
  ContextConfig,
  ContextQuery,
  KnowledgeItem,
  WorkflowContext,
  ToolContext,
  contextConfigSchema
} from './types';
import { KnowledgeBase } from '../knowledge-base';
import { WorkflowManager } from '../workflow-manager';
import { TaskAnalyzer } from '../task-analyzer';
import { MemoryIntegration, MemoryIntegrationConfig } from './memory-integration';

// 上下文构建器
export class ContextBuilder extends EventEmitter {
  private config: Required<ContextConfig>;
  private cache = new Map<string, Context>();
  private itemCache = new Map<string, ContextItem>();
  private memoryIntegration?: MemoryIntegration;
  
  constructor(
    private knowledgeBase: KnowledgeBase,
    private workflowManager: WorkflowManager,
    private taskAnalyzer: TaskAnalyzer,
    config: ContextConfig & { memory?: MemoryIntegrationConfig } = {}
  ) {
    super();
    
    // 验证并设置默认配置
    const { memory, ...contextConfig } = config;
    const validatedConfig = contextConfigSchema.parse(contextConfig);
    this.config = {
      maxSize: 100 * 1024,        // 100KB
      maxItems: 100,
      compressionThreshold: 50 * 1024, // 50KB
      relevanceThreshold: 0.3,
      cacheTTL: 5 * 60 * 1000,    // 5分钟
      enableCompression: true,
      enableCache: true,
      enableOptimization: true,
      ...validatedConfig
    };
    
    // 初始化 Memory 集成（如果提供了配置）
    if (memory && memory.enabled !== false) {
      this.memoryIntegration = new MemoryIntegration(memory);
      logger.info('Memory integration enabled in ContextBuilder');
    }
  }
  
  /**
   * 构建上下文
   */
  async build(input: {
    task?: any;
    query?: string;
    history?: any[];
    tools?: ToolContext[];
    metadata?: Record<string, any>;
  }): Promise<Context> {
    const startTime = Date.now();
    const contextId = uuidv4();
    
    logger.debug('Building context:', { contextId, input });
    
    try {
      const items: ContextItem[] = [];
      
      // 1. 添加系统提示词
      if (input.task) {
        const systemPrompt = await this.buildSystemPrompt(input.task);
        if (systemPrompt) {
          items.push(systemPrompt);
        }
      }
      
      // 2. 搜索相关知识
      if (input.query || input.task) {
        const knowledge = await this.searchKnowledge(
          input.query || input.task.description
        );
        items.push(...knowledge);
      }
      
      // 3. 选择合适的工作流
      if (input.task) {
        const workflow = await this.selectWorkflow(input.task);
        if (workflow) {
          items.push(workflow);
        }
      }
      
      // 4. 添加历史记录
      if (input.history && input.history.length > 0) {
        const historyItems = await this.processHistory(input.history);
        items.push(...historyItems);
      }
      
      // 5. 添加工具调用结果
      if (input.tools && input.tools.length > 0) {
        const toolItems = this.processTools(input.tools);
        items.push(...toolItems);
      }
      
      // 6. 添加当前状态
      const stateItem = await this.getCurrentState(input.metadata);
      if (stateItem) {
        items.push(stateItem);
      }
      
      // 7. 集成 Memory 系统（如果启用）
      if (this.memoryIntegration && this.memoryIntegration.isEnabled()) {
        try {
          // 搜索相关记忆
          if (input.query || input.task?.description) {
            const memoryItems = await this.memoryIntegration.searchMemory(
              input.query || input.task.description
            );
            items.push(...memoryItems);
          }
          
          // 获取任务建议
          if (input.task) {
            const taskSuggestion = await this.memoryIntegration.getTaskSuggestions(input.task);
            if (taskSuggestion) {
              items.push(taskSuggestion);
            }
            
            // 获取 Agent 推荐
            const agentRecommendation = await this.memoryIntegration.getAgentRecommendation(input.task);
            if (agentRecommendation) {
              items.push(agentRecommendation);
            }
          }
          
          // 获取用户偏好
          const preferences = await this.memoryIntegration.getUserPreferences();
          if (preferences) {
            items.push(preferences);
          }
          
          // 获取历史相关记忆
          if (input.history && input.history.length > 0) {
            const historicalMemory = await this.memoryIntegration.getHistoricalMemory(input.history);
            items.push(...historicalMemory);
          }
          
          logger.debug(`Added ${items.length - stateItem ? 1 : 0} memory-enhanced items`);
        } catch (error) {
          logger.error('Memory integration failed:', error);
          // 继续执行，不影响基础功能
        }
      }
      
      // 创建初始上下文
      let context: Context = {
        id: contextId,
        items,
        totalSize: this.calculateTotalSize(items),
        metadata: {
          created: Date.now(),
          updated: Date.now(),
          compressed: false,
          optimized: false,
          memoryEnhanced: this.memoryIntegration?.isEnabled() || false,
          ...input.metadata
        }
      };
      
      // 优化上下文
      if (this.config.enableOptimization) {
        context = await this.optimize(context);
      }
      
      // 压缩上下文
      if (this.config.enableCompression && 
          context.totalSize > this.config.compressionThreshold) {
        context = await this.compress(context);
      }
      
      // 缓存上下文
      if (this.config.enableCache) {
        this.cacheContext(context);
      }
      
      const duration = Date.now() - startTime;
      logger.info(`Context built in ${duration}ms:`, {
        id: context.id,
        itemCount: context.items.length,
        totalSize: context.totalSize,
        compressed: context.metadata.compressed,
        optimized: context.metadata.optimized,
        memoryEnhanced: context.metadata.memoryEnhanced
      });
      
      // 保存上下文到记忆系统（异步，不阻塞返回）
      if (this.memoryIntegration && this.memoryIntegration.isEnabled()) {
        this.memoryIntegration.saveToMemory(context, {
          contextId: context.id,
          task: input.task?.name,
          query: input.query,
          itemCount: context.items.length,
          totalSize: context.totalSize
        }).catch(error => {
          logger.error('Failed to save context to memory:', error);
        });
      }
      
      this.emit('built', context);
      return context;
      
    } catch (error) {
      logger.error('Failed to build context:', error);
      this.emit('error', error);
      throw error;
    }
  }
  
  /**
   * 查询上下文项
   */
  async query(context: Context, query: ContextQuery): Promise<ContextItem[]> {
    let items = [...context.items];
    
    // 按类型过滤
    if (query.types && query.types.length > 0) {
      items = items.filter(item => query.types!.includes(item.type));
    }
    
    // 按来源过滤
    if (query.sources && query.sources.length > 0) {
      items = items.filter(item => 
        item.source && query.sources!.includes(item.source)
      );
    }
    
    // 按相关性过滤
    if (query.minRelevance !== undefined) {
      items = items.filter(item => 
        (item.relevance || 0) >= query.minRelevance!
      );
    }
    
    // 按时间过滤
    if (query.maxAge !== undefined) {
      const minTimestamp = Date.now() - query.maxAge;
      items = items.filter(item => item.timestamp >= minTimestamp);
    }
    
    // 按关键词过滤
    if (query.keywords && query.keywords.length > 0) {
      items = items.filter(item => {
        const content = JSON.stringify(item.content).toLowerCase();
        return query.keywords!.some(keyword => 
          content.includes(keyword.toLowerCase())
        );
      });
    }
    
    // 排序（按优先级和相关性）
    items.sort((a, b) => {
      const scoreA = a.priority + (a.relevance || 0) * 100;
      const scoreB = b.priority + (b.relevance || 0) * 100;
      return scoreB - scoreA;
    });
    
    // 分页
    if (query.offset !== undefined || query.limit !== undefined) {
      const offset = query.offset || 0;
      const limit = query.limit || items.length;
      items = items.slice(offset, offset + limit);
    }
    
    return items;
  }
  
  /**
   * 添加上下文项
   */
  async addItem(
    context: Context,
    item: Omit<ContextItem, 'id' | 'timestamp' | 'size'>
  ): Promise<Context> {
    const newItem: ContextItem = {
      ...item,
      id: uuidv4(),
      timestamp: Date.now(),
      size: this.calculateItemSize(item)
    };
    
    const updatedContext: Context = {
      ...context,
      items: [...context.items, newItem],
      totalSize: context.totalSize + newItem.size,
      metadata: {
        ...context.metadata,
        updated: Date.now()
      }
    };
    
    // 检查是否需要优化
    if (this.config.enableOptimization && 
        (updatedContext.items.length > this.config.maxItems ||
         updatedContext.totalSize > this.config.maxSize)) {
      return this.optimize(updatedContext);
    }
    
    return updatedContext;
  }
  
  /**
   * 移除上下文项
   */
  async removeItem(context: Context, itemId: string): Promise<Context> {
    const item = context.items.find(i => i.id === itemId);
    if (!item) {
      return context;
    }
    
    return {
      ...context,
      items: context.items.filter(i => i.id !== itemId),
      totalSize: context.totalSize - (item.size || 0),
      metadata: {
        ...context.metadata,
        updated: Date.now()
      }
    };
  }
  
  /**
   * 更新上下文项
   */
  async updateItem(
    context: Context,
    itemId: string,
    updates: Partial<ContextItem>
  ): Promise<Context> {
    const itemIndex = context.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) {
      return context;
    }
    
    const oldItem = context.items[itemIndex];
    const newItem: ContextItem = {
      ...oldItem,
      ...updates,
      id: oldItem.id,
      timestamp: Date.now()
    };
    
    // 重新计算大小
    if (updates.content !== undefined) {
      newItem.size = this.calculateItemSize(newItem);
    }
    
    const newItems = [...context.items];
    newItems[itemIndex] = newItem;
    
    return {
      ...context,
      items: newItems,
      totalSize: context.totalSize - (oldItem.size || 0) + (newItem.size || 0),
      metadata: {
        ...context.metadata,
        updated: Date.now()
      }
    };
  }
  
  /**
   * 合并上下文
   */
  async merge(...contexts: Context[]): Promise<Context> {
    if (contexts.length === 0) {
      throw new Error('No contexts to merge');
    }
    
    if (contexts.length === 1) {
      return contexts[0];
    }
    
    // 收集所有项
    const allItems: ContextItem[] = [];
    const itemIds = new Set<string>();
    
    for (const context of contexts) {
      for (const item of context.items) {
        // 避免重复
        if (!itemIds.has(item.id)) {
          allItems.push(item);
          itemIds.add(item.id);
        }
      }
    }
    
    // 创建合并后的上下文
    let mergedContext: Context = {
      id: uuidv4(),
      items: allItems,
      totalSize: this.calculateTotalSize(allItems),
      metadata: {
        created: Date.now(),
        updated: Date.now(),
        compressed: false,
        optimized: false,
        mergedFrom: contexts.map(c => c.id)
      }
    };
    
    // 优化合并后的上下文
    if (this.config.enableOptimization) {
      mergedContext = await this.optimize(mergedContext);
    }
    
    return mergedContext;
  }
  
  /**
   * 清理缓存
   */
  cleanCache(): void {
    const now = Date.now();
    
    // 清理过期的上下文缓存
    for (const [id, context] of this.cache) {
      if (now - context.metadata.updated > this.config.cacheTTL) {
        this.cache.delete(id);
      }
    }
    
    // 清理过期的项目缓存
    for (const [id, item] of this.itemCache) {
      if (now - item.timestamp > this.config.cacheTTL) {
        this.itemCache.delete(id);
      }
    }
  }
  
  // 私有方法
  
  private async buildSystemPrompt(task: any): Promise<ContextItem | null> {
    try {
      const analysis = await this.taskAnalyzer.analyze(task);
      
      if (!analysis.suggestedPrompt) {
        return null;
      }
      
      return {
        id: uuidv4(),
        type: ContextType.SYSTEM,
        content: analysis.suggestedPrompt,
        priority: 100,
        relevance: 1.0,
        timestamp: Date.now(),
        source: 'task-analyzer',
        metadata: {
          taskType: analysis.type,
          complexity: analysis.complexity
        },
        size: 0 // 将在后面计算
      };
    } catch (error) {
      logger.warn('Failed to build system prompt:', error);
      return null;
    }
  }
  
  private async searchKnowledge(query: string): Promise<ContextItem[]> {
    try {
      const results = await this.knowledgeBase.search(query, {
        limit: 10,
        threshold: this.config.relevanceThreshold
      });
      
      return results.map((item: KnowledgeItem) => ({
        id: uuidv4(),
        type: ContextType.KNOWLEDGE,
        content: item,
        priority: 70,
        relevance: item.score || 0.5,
        timestamp: Date.now(),
        source: 'knowledge-base',
        metadata: {
          category: item.category,
          tags: item.tags
        },
        size: 0
      }));
    } catch (error) {
      logger.warn('Failed to search knowledge:', error);
      return [];
    }
  }
  
  private async selectWorkflow(task: any): Promise<ContextItem | null> {
    try {
      const workflow = await this.workflowManager.selectWorkflow(task);
      
      if (!workflow) {
        return null;
      }
      
      const workflowContext: WorkflowContext = {
        id: workflow.id,
        name: workflow.name,
        description: workflow.description,
        steps: workflow.steps.map(step => ({
          id: step.id,
          name: step.name,
          description: step.description,
          completed: false
        })),
        variables: {},
        status: 'pending'
      };
      
      return {
        id: uuidv4(),
        type: ContextType.WORKFLOW,
        content: workflowContext,
        priority: 80,
        relevance: 0.8,
        timestamp: Date.now(),
        source: 'workflow-manager',
        metadata: {
          workflowId: workflow.id,
          category: workflow.category
        },
        size: 0
      };
    } catch (error) {
      logger.warn('Failed to select workflow:', error);
      return null;
    }
  }
  
  private async processHistory(history: any[]): Promise<ContextItem[]> {
    // 只保留最近的历史记录
    const recentHistory = history.slice(-20);
    
    return recentHistory.map((entry, index) => ({
      id: uuidv4(),
      type: ContextType.HISTORY,
      content: entry,
      priority: 50 + index, // 越新的历史优先级越高
      relevance: 0.6,
      timestamp: entry.timestamp || Date.now(),
      source: 'history',
      metadata: {
        index,
        role: entry.role
      },
      size: 0
    }));
  }
  
  private processTools(tools: ToolContext[]): ContextItem[] {
    return tools.map(tool => ({
      id: uuidv4(),
      type: ContextType.TOOL,
      content: tool,
      priority: 60,
      relevance: 0.7,
      timestamp: tool.timestamp,
      source: 'tool-execution',
      metadata: {
        toolName: tool.toolName,
        success: !tool.error,
        duration: tool.duration
      },
      size: 0
    }));
  }
  
  private async getCurrentState(metadata?: Record<string, any>): Promise<ContextItem | null> {
    try {
      // 收集当前状态信息
      const state = {
        timestamp: Date.now(),
        environment: process.env.NODE_ENV,
        ...metadata
      };
      
      return {
        id: uuidv4(),
        type: ContextType.STATE,
        content: state,
        priority: 40,
        relevance: 0.5,
        timestamp: Date.now(),
        source: 'system',
        metadata: {},
        size: 0
      };
    } catch (error) {
      logger.warn('Failed to get current state:', error);
      return null;
    }
  }
  
  private calculateItemSize(item: any): number {
    return JSON.stringify(item).length;
  }
  
  private calculateTotalSize(items: ContextItem[]): number {
    return items.reduce((total, item) => {
      if (!item.size) {
        item.size = this.calculateItemSize(item);
      }
      return total + item.size;
    }, 0);
  }
  
  private async optimize(context: Context): Promise<Context> {
    // 将在 context-optimizer.ts 中实现
    return context;
  }
  
  private async compress(context: Context): Promise<Context> {
    // 将在 context-compressor.ts 中实现
    return context;
  }
  
  private cacheContext(context: Context): void {
    this.cache.set(context.id, context);
    
    // 缓存单个项目
    for (const item of context.items) {
      this.itemCache.set(item.id, item);
    }
    
    // 定期清理缓存
    if (this.cache.size > 100) {
      this.cleanCache();
    }
  }
}